import { useState, type PointerEvent as ReactPointerEvent } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import {
  isListItem,
  layerLabel,
  ownerOf,
  type Inspection,
} from '@/designs-dev/inspect/fiber';
import { applyTweak, type TweakCss } from '@/designs-dev/inspect/tweaks';

// On-canvas handles for the selected node in drill mode: drag its padding, drag
// the gap between its children, drag it along its parent to reorder.
//
// Every drag is a tweak like the ones the panel's steppers make: applied as inline
// style, snapped to spacing tokens, thrown away on refresh, and copied out as an
// instruction. The canvas is a faster way to ask the question, not a place where
// the answer is saved.
//
// Rendered inside InspectOverlay's portal, so it shares the screen's logical
// coordinate space. Every node carries `data-drill-handle` (interactive) or
// `data-drill-layer` (decoration), which the overlay's click and hover listeners
// skip and the flow measurements leave out.

type Rect = { top: number; left: number; width: number; height: number };
type Side = 'top' | 'right' | 'bottom' | 'left';

// Steps a drag snaps through. Resolved through theme.spacing, so they land on the
// same px under Loft's 8px scale and llama's token array.
const STEPS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 10, 12];

// The thinnest a handle is drawn, so a padding of 0 can still be grabbed.
const HIT = 8;

type Flow = {
  container: HTMLElement;
  row: boolean;
  // In-flow children, in the order they currently show.
  children: HTMLElement[];
  // A Stack using `spacing` spaces its children with margins, not `gap`. A gap
  // tweak or a reorder on one has to switch it to gap while previewing, or the
  // margins stay where they were and the change reads wrong.
  marginMode: boolean;
  isStack: boolean;
};

const flowOf = (container: HTMLElement | null): Flow | null => {
  if (!container) return null;
  const cs = getComputedStyle(container);
  if (!/flex/.test(cs.display)) return null;
  const row = cs.flexDirection.startsWith('row');
  const children = [...container.children]
    .filter((c): c is HTMLElement => c instanceof HTMLElement)
    .filter((c) => !c.dataset.drillHandle && !c.dataset.drillLayer)
    .filter((c) => {
      const s = getComputedStyle(c);
      return (
        s.display !== 'none' &&
        s.position !== 'absolute' &&
        s.position !== 'fixed'
      );
    })
    .sort((a, b) => {
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      return row ? ra.left - rb.left : ra.top - rb.top;
    });
  const isStack = container.classList.contains('MuiStack-root');
  const noGap =
    (parseFloat(cs.rowGap) || 0) === 0 && (parseFloat(cs.columnGap) || 0) === 0;
  return { container, row, children, marginMode: isStack && noGap, isStack };
};

// The declarations that set a flow's gap, zeroing a Stack's margins when that is
// where its spacing came from.
const gapCss = (flow: Flow, px: number): TweakCss[] => [
  { property: 'gap', value: `${px}px` },
  ...(flow.marginMode
    ? flow.children.map((element) => ({
        element,
        property: flow.row ? 'margin-left' : 'margin-top',
        value: '0px',
      }))
    : []),
];

const paddingTarget = (
  side: Side,
  e: { altKey: boolean; shiftKey: boolean },
): { prop: string; sides: Side[] } => {
  if (e.shiftKey)
    return { prop: 'p', sides: ['top', 'right', 'bottom', 'left'] };
  if (e.altKey)
    return side === 'top' || side === 'bottom'
      ? { prop: 'py', sides: ['top', 'bottom'] }
      : { prop: 'px', sides: ['left', 'right'] };
  return { prop: `p${side[0]}`, sides: [side] };
};

const CURSOR: Record<Side, string> = {
  top: 'ns-resize',
  bottom: 'ns-resize',
  left: 'ew-resize',
  right: 'ew-resize',
};

const DirectEdit = ({
  current,
  file,
  toRect,
  scale,
}: {
  current: Inspection;
  file?: string;
  toRect: (el: HTMLElement) => Rect;
  scale: number;
}) => {
  const theme = useTheme();
  const [readout, setReadout] = useState<{
    text: string;
    top: number;
    left: number;
  } | null>(null);
  const el = current.domNode;
  if (!el) return null;

  const snap = (px: number) => {
    let step = STEPS[0];
    let best = Infinity;
    for (const s of STEPS) {
      const d = Math.abs(parseFloat(theme.spacing(s)) - px);
      if (d < best) {
        best = d;
        step = s;
      }
    }
    return { step, px: parseFloat(theme.spacing(step)) };
  };

  // What the call site passes for a prop, for the instruction's "from". `unset`
  // when it passes nothing, as the panel's steppers say it.
  const originFor = (prop: string) =>
    [...(current.layoutProps ?? []), ...current.props].find(
      (p) => p.name === prop,
    )?.value ?? 'unset';

  // Track a drag in the screen's logical pixels, whatever the frame's scale.
  const startDrag = (
    e: ReactPointerEvent,
    onMove: (dx: number, dy: number, ev: PointerEvent) => void,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const y0 = e.clientY;
    const move = (ev: PointerEvent) =>
      onMove((ev.clientX - x0) / scale, (ev.clientY - y0) / scale, ev);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setReadout(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const r = toRect(el);
  const cs = getComputedStyle(el);
  const num = (p: string) => parseFloat(cs.getPropertyValue(p)) || 0;
  const border = {
    top: num('border-top-width'),
    right: num('border-right-width'),
    bottom: num('border-bottom-width'),
    left: num('border-left-width'),
  };
  const pad = {
    top: num('padding-top'),
    right: num('padding-right'),
    bottom: num('padding-bottom'),
    left: num('padding-left'),
  };

  // --- Padding ---------------------------------------------------------------

  // Each band covers the padding on one side, inside the border. Dragging toward
  // the content adds padding. Alt takes the opposite side with it (px / py),
  // shift takes all four (p).
  const padBand = (side: Side): Rect => {
    const innerW = r.width - border.left - border.right;
    const innerH = r.height - border.top - border.bottom;
    const t = Math.max(pad[side], HIT);
    switch (side) {
      case 'top':
        return {
          top: r.top + border.top,
          left: r.left + border.left,
          width: innerW,
          height: t,
        };
      case 'bottom':
        return {
          top: r.top + r.height - border.bottom - t,
          left: r.left + border.left,
          width: innerW,
          height: t,
        };
      case 'left':
        return {
          top: r.top + border.top,
          left: r.left + border.left,
          width: t,
          height: innerH,
        };
      case 'right':
        return {
          top: r.top + border.top,
          left: r.left + r.width - border.right - t,
          width: t,
          height: innerH,
        };
    }
  };

  const onPad = (side: Side) => (e: ReactPointerEvent) => {
    const target = paddingTarget(side, e);
    const start = pad[side];
    const sign = side === 'top' || side === 'left' ? 1 : -1;
    const from = originFor(target.prop);
    // Padding was showing on a side this drag touches, so a 0 removes it.
    const overrides = target.sides.some((s) => pad[s] > 0.5);
    const band = padBand(side);
    let last: number | null = null;
    startDrag(e, (dx, dy) => {
      const d = side === 'top' || side === 'bottom' ? dy : dx;
      const { step, px } = snap(Math.max(0, start + sign * d));
      setReadout({
        text: `${target.prop}={${step}} · ${px}px`,
        top: band.top + band.height / 2,
        left: band.left + band.width / 2,
      });
      if (step === last) return;
      last = step;
      applyTweak({
        component: current.name,
        file,
        kind: 'spacing',
        prop: target.prop,
        from,
        to: String(step),
        overrides,
        element: el,
        css: target.sides.map((s) => ({
          property: `padding-${s}`,
          value: `${px}px`,
        })),
      });
    });
  };

  // --- Gap -------------------------------------------------------------------

  // A band in each space between neighbouring children on the same line. All of
  // them drive the one gap, so any is a handle for it.
  const flow = flowOf(el);
  const gapBands: Rect[] = [];
  let gapStart = 0;
  if (flow && flow.children.length > 1) {
    const rects = flow.children.map(toRect);
    for (let i = 1; i < rects.length; i++) {
      const a = rects[i - 1];
      const b = rects[i];
      if (flow.row) {
        const sameLine = a.top < b.top + b.height && b.top < a.top + a.height;
        if (!sameLine) continue;
        const space = b.left - (a.left + a.width);
        if (gapBands.length === 0) gapStart = space;
        const w = Math.max(space, HIT);
        gapBands.push({
          top: Math.min(a.top, b.top),
          left: a.left + a.width + space / 2 - w / 2,
          width: w,
          height:
            Math.max(a.top + a.height, b.top + b.height) -
            Math.min(a.top, b.top),
        });
      } else {
        const sameColumn =
          a.left < b.left + b.width && b.left < a.left + a.width;
        if (!sameColumn) continue;
        const space = b.top - (a.top + a.height);
        if (gapBands.length === 0) gapStart = space;
        const h = Math.max(space, HIT);
        gapBands.push({
          top: a.top + a.height + space / 2 - h / 2,
          left: r.left + border.left + pad.left,
          width: r.width - border.left - border.right - pad.left - pad.right,
          height: h,
        });
      }
    }
  }

  const onGap = (band: Rect) => (e: ReactPointerEvent) => {
    if (!flow) return;
    // A Stack's spacing is its `spacing` prop; anything else writes `gap`.
    const prop = flow.isStack ? 'spacing' : 'gap';
    const from = originFor(prop);
    const start = Math.max(0, gapStart);
    let last: number | null = null;
    startDrag(e, (dx, dy) => {
      const { step, px } = snap(Math.max(0, start + (flow.row ? dx : dy)));
      setReadout({
        text: `${prop}={${step}} · ${px}px`,
        top: band.top + band.height / 2,
        left: band.left + band.width / 2,
      });
      if (step === last) return;
      last = step;
      applyTweak({
        component: current.name,
        file,
        kind: 'spacing',
        prop,
        from,
        to: String(step),
        overrides: start > 0.5,
        element: el,
        css: gapCss(flow, px),
      });
    });
  };

  // --- Reorder ---------------------------------------------------------------

  // A grip on the selection when its parent lays its children out in a row or a
  // column. Dragging it along that axis moves it among its siblings.
  const parentFlow = flowOf(el.parentElement);
  const canReorder =
    !!parentFlow &&
    parentFlow.children.length > 1 &&
    parentFlow.children.includes(el);

  const onGrip = (e: ReactPointerEvent) => {
    if (!parentFlow) return;
    const { container, row } = parentFlow;
    const items = parentFlow.children;
    const owner = ownerOf(container);
    const labels = new Map(items.map((c) => [c, layerLabel(c)]));
    const fromOrder = items.map((c) => labels.get(c)!);
    const others = items.filter((c) => c !== el);
    // Where the others sit along the axis at the start, in client pixels. Taken
    // once, so the siblings sliding out of the way do not move the targets.
    const mids = others.map((c) => {
      const b = c.getBoundingClientRect();
      return row ? b.left + b.width / 2 : b.top + b.height / 2;
    });
    const listy = items.some((c) => isListItem(c, container));
    // The visual gap now, so a margin-spaced Stack keeps its spacing once its
    // margins are swapped for gap and the first child changes.
    const a = items[0].getBoundingClientRect();
    const b = items[1].getBoundingClientRect();
    const gapPx = Math.max(
      0,
      (row ? b.left - (a.left + a.width) : b.top - (a.top + a.height)) / scale,
    );
    let last = items.indexOf(el);
    startDrag(e, (_dx, _dy, ev) => {
      const at = row ? ev.clientX : ev.clientY;
      const index = mids.filter((m) => m < at).length;
      const order = [...others];
      order.splice(index, 0, el);
      const box = toRect(el);
      setReadout({
        text: `${index + 1} of ${items.length}`,
        top: box.top - 14,
        left: box.left + box.width / 2,
      });
      if (index === last) return;
      last = index;
      applyTweak({
        component: owner.name,
        file: owner.file,
        kind: 'order',
        prop: 'order',
        from: `[${fromOrder.join(', ')}]`,
        to: `[${order.map((c) => labels.get(c)!).join(', ')}]`,
        note: listy
          ? 'These children are mapped from a list: reorder the array (content or fixtures module), not the JSX.'
          : undefined,
        element: container,
        css: [
          ...order.map((element, i) => ({
            element,
            property: 'order',
            value: String(i),
          })),
          ...(parentFlow.marginMode ? gapCss(parentFlow, gapPx) : []),
        ],
      });
    });
  };

  const grip = (() => {
    if (!canReorder || !parentFlow) return null;
    const size = 20;
    // Straddles the edge the item moves along, kept inside the screen.
    const top = parentFlow.row
      ? Math.max(2, r.top - size / 2)
      : r.top + r.height / 2 - size / 2;
    const left = parentFlow.row
      ? r.left + r.width / 2 - size / 2
      : Math.max(2, r.left - size / 2);
    return { top, left, size };
  })();

  // --- Drawing ---------------------------------------------------------------

  const coral = theme.palette.custom.coral[500];
  const marine = theme.palette.custom.marineGreen[500];

  // A tinted strip over the space it controls, with a solid pill in the middle
  // that says "grab here". The tint alone disappeared on a zero padding and on
  // warm surfaces, so the pill is what makes a handle findable.
  const band = (
    key: string,
    rect: Rect,
    colour: string,
    cursor: string,
    onPointerDown: (e: ReactPointerEvent) => void,
  ) => {
    // The pill runs along the strip, across the direction of the drag.
    const horizontal = cursor === 'ns-resize';
    return (
      <Box
        key={key}
        data-drill-handle
        onPointerDown={onPointerDown}
        style={{ '--drill-cursor': cursor } as React.CSSProperties}
        sx={{
          position: 'absolute',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          zIndex: 32,
          backgroundColor: alpha(colour, 0.22),
          outline: `1px dashed ${alpha(colour, 0.7)}`,
          outlineOffset: -1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          '&:hover': { backgroundColor: alpha(colour, 0.42) },
        }}
      >
        <Box
          sx={{
            width: horizontal ? 20 : 4,
            height: horizontal ? 4 : 20,
            borderRadius: 2,
            backgroundColor: colour,
            pointerEvents: 'none',
          }}
        />
      </Box>
    );
  };

  return (
    <>
      {(['top', 'right', 'bottom', 'left'] as Side[]).map((side) =>
        band(`pad-${side}`, padBand(side), coral, CURSOR[side], onPad(side)),
      )}
      {flow &&
        gapBands.map((g, i) =>
          band(
            `gap-${i}`,
            g,
            marine,
            flow.row ? 'ew-resize' : 'ns-resize',
            onGap(g),
          ),
        )}
      {grip && (
        <Box
          data-drill-handle
          onPointerDown={onGrip}
          style={{ '--drill-cursor': 'grab' } as React.CSSProperties}
          sx={{
            position: 'absolute',
            top: grip.top,
            left: grip.left,
            width: grip.size,
            height: grip.size,
            zIndex: 33,
            borderRadius: '50%',
            bgcolor: 'custom.coral.600',
            color: 'custom.neutral.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
          }}
        >
          <Icon
            icon={(parentFlow?.row ? 'ellipsis' : 'ellipsis-vertical') as never}
            fontSize={12}
          />
        </Box>
      )}
      {readout && (
        <Box
          data-drill-layer
          sx={{
            position: 'absolute',
            top: readout.top,
            left: readout.left,
            transform: 'translate(-50%, -50%)',
            zIndex: 34,
            pointerEvents: 'none',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'custom.charcoal',
            color: 'custom.neutral.50',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant='mono' sx={{ color: 'inherit' }}>
            {readout.text}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default DirectEdit;
